import React from 'react';
import { fechaCorta } from '../utils/fechas';
import { useIdioma } from '../i18n/idioma';
import { useProjects } from '../contexts/ProjectsContext';
import { useEstimates } from '../contexts/EstimatesContext';

import { useAuth } from '../contexts/AuthContext';
import { Calendar, FileText, Briefcase, FileSignature, CircleDashed, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { StatusDot } from '../components/ui/StatusDot';

const ClientDashboardView: React.FC = () => {
    const { t } = useIdioma();
    const { projects } = useProjects();
    const { estimates } = useEstimates();
    const serviceVisits: any[] = []; // Service visits removed
    const { clientId } = useAuth(); // The ID of the currently logged-in Client user

    // Fallback if somehow rendered without a valid client ID
    if (!clientId) {
        return (
            <div className="bg-white rounded-2xl border border-acero-200">
                <EmptyState
                    variant="error"
                    icon={<AlertTriangle size={18} />}
                    title={t('cliente.panel.error_cuenta_titulo')}
                    description={t('cliente.panel.error_cuenta_descripcion')}
                />
            </div>
        );
    }

    // Filter all data for this specific client
    const clientProjects = projects.filter(p => p.clientId === clientId);
    const clientEstimates = estimates.filter(e => e.clientId === clientId);

    // Sort visits by date (upcoming first)
    const upcomingVisits = serviceVisits
        .filter(v => v.clientId === clientId && v.status !== 'Completed' && v.status !== 'Skipped')
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .slice(0, 5); // Show next 5

    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            {/* Welcome Banner */}
            <div className="bg-navy-850 rounded-2xl p-8">
                <h1 className="text-[22px] sm:text-[26px] leading-tight font-semibold tracking-tight text-white mb-2">
                    {t('cliente.panel.bienvenida')}
                </h1>
                <p className="text-sm text-acero-300 max-w-xl leading-relaxed">
                    {t('cliente.panel.bienvenida_descripcion')}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column (Wider for Projects/Estimates) */}
                <div className="lg:col-span-2 space-y-8">

                    {/* Active Projects Section */}
                    <section>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-base font-semibold tracking-tight text-navy-950 flex items-center gap-2">
                                <Briefcase className="text-acero-400" size={16} /> {t('comun.proyectosActivos')}
                            </h2>
                        </div>

                        {clientProjects.length > 0 ? (
                            <div className="grid grid-cols-1 gap-4">
                                {clientProjects.map(project => (
                                    <div
                                        key={project.id}
                                        className="bg-white rounded-2xl border border-acero-200 p-5 hover:border-acero-300 transition-colors duration-150"
                                    >
                                        <div className="flex justify-between items-start gap-4 mb-3">
                                            <div className="min-w-0">
                                                <h3 className="font-semibold tracking-tight text-[15px] text-navy-950">{project.name}</h3>
                                                <p className="text-sm text-acero-500 line-clamp-2 mt-0.5">{project.description}</p>
                                            </div>
                                            <StatusDot className="shrink-0 mt-1">{project.status}</StatusDot>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="mt-4">
                                            <div className="flex justify-between items-baseline mb-1.5">
                                                <span className="label-ui">{t('cliente.panel.avance')}</span>
                                                <span className="font-data text-[11px] tabular-nums text-navy-950">{project.completionPercent}%</span>
                                            </div>
                                            <div className="w-full bg-acero-100 rounded-full h-1.5 overflow-hidden">
                                                <div
                                                    className="bg-navy-800 h-1.5 rounded-full transition-[width] duration-500 ease-out"
                                                    style={{ width: `${project.completionPercent}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-white border border-acero-200 rounded-2xl">
                                <EmptyState
                                    icon={<Briefcase size={18} />}
                                    title={t('cliente.panel.proyectos_vacio_titulo')}
                                    description={t('cliente.panel.proyectos_vacio_descripcion')}
                                />
                            </div>
                        )}
                    </section>

                    {/* Estimates Section */}
                    <section>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-base font-semibold tracking-tight text-navy-950 flex items-center gap-2">
                                <FileText className="text-acero-400" size={16} /> {t('cliente.panel.estimados_titulo')}
                            </h2>
                        </div>

                        {clientEstimates.length > 0 ? (
                            <div className="bg-white rounded-2xl border border-acero-200 overflow-hidden">
                                <ul className="divide-y divide-acero-100">
                                    {clientEstimates.map(est => (
                                        <li key={est.id} className="px-4 py-3 hover:bg-acero-50 transition-colors duration-150 flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 shrink-0 rounded-lg grid place-items-center bg-acero-100 text-acero-500">
                                                    {est.status === 'Accepted' ? <FileSignature size={16} /> : <FileText size={16} />}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-[13px] font-medium text-navy-950 truncate">
                                                        {est.proposalSettings?.title || `${t('cliente.panel.estimado_para')}${est.clientName}`}
                                                    </p>
                                                    <p className="text-xs text-acero-500">
                                                        {t('cliente.panel.creado')}<span className="font-data tabular-nums">{fechaCorta(est.dateCreated)}</span>
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="font-data tabular-nums text-[13px] font-semibold text-navy-950">
                                                    ${est.total.toFixed(2)}
                                                </p>
                                                <StatusDot className="mt-0.5">{est.status}</StatusDot>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="bg-white border border-acero-200 rounded-2xl">
                                <EmptyState
                                    icon={<FileText size={18} />}
                                    title={t('cliente.panel.estimados_vacio_titulo')}
                                    description={t('cliente.panel.estimados_vacio_descripcion')}
                                />
                            </div>
                        )}
                    </section>
                </div>

                {/* Right Column (Sidebar) */}
                <div className="space-y-8">
                    {/* Upcoming Service Visits */}
                    <section className="bg-white rounded-2xl border border-acero-200 p-6 sticky top-24">
                        <h2 className="text-base font-semibold tracking-tight text-navy-950 flex items-center gap-2 mb-5">
                            <Calendar className="text-acero-400" size={16} /> {t('cliente.panel.servicios_titulo')}
                        </h2>

                        {upcomingVisits.length > 0 ? (
                            <div className="space-y-3">
                                {upcomingVisits.map(visit => {
                                    const visitDate = new Date(visit.date);
                                    const isToday = visitDate.toDateString() === new Date().toDateString();

                                    return (
                                        <div
                                            key={visit.id}
                                            className={`flex gap-4 p-3 rounded-xl border-l-2 ${isToday ? 'bg-aviso-bg border-aviso' : 'bg-acero-50 border-acero-300'}`}
                                        >
                                            <div className="flex flex-col items-center justify-center min-w-[3rem] shrink-0">
                                                <span className="label-ui">
                                                    {visitDate.toLocaleString('default', { month: 'short' })}
                                                </span>
                                                <span className={`font-data text-lg tabular-nums leading-none mt-1 ${isToday ? 'text-aviso' : 'text-navy-950'}`}>
                                                    {visitDate.getDate()}
                                                </span>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[13px] font-medium text-navy-950 mb-1">
                                                    {isToday ? t('cliente.panel.visita_hoy') : t('cliente.panel.visita_proxima')}
                                                </p>
                                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-acero-600">
                                                    {visit.status === 'On Site'
                                                        ? <CircleDashed size={12} className="animate-spin text-navy-600" />
                                                        : <Clock size={12} className="text-acero-400" />}
                                                    {visit.status}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <EmptyState
                                icon={<Calendar size={18} />}
                                title={t('cliente.panel.servicios_vacio_titulo')}
                                description={t('cliente.panel.servicios_vacio_descripcion')}
                                className="!py-8"
                            />
                        )}

                        <div className="mt-6 pt-4 border-t border-acero-200">
                            <Button variant="outline" className="w-full">
                                {t('cliente.panel.pedir_servicio')}
                            </Button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default ClientDashboardView;
